import React, { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { MagnifyingGlassIcon, ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline'
import { proVerticals } from '../../Data/proVerticals'
import { useProSelection } from '../../services/products'
import ImagePlaceholder from './ImagePlaceholder'
import swryaLogo from '../../Media/swrya-logo.png'

const verticalOf = (card) => proVerticals.find((v) => v.match.test(card.operacion))?.id
const LEVELS = [1, 2, 3]



const levelStyles = {
  1: { text: 'text-gray-500', bar: 'bg-gray-400', badge: 'bg-gray-100 text-gray-600' },
  2: { text: 'text-electric-600', bar: 'bg-electric-500', badge: 'bg-electric-50 text-electric-700' },
  3: { text: 'text-red-700', bar: 'bg-red-700', badge: 'bg-red-50 text-red-700' },
}

function ProCard({ card, t }) {
  const imagen = card.specs?.find(([key]) => key === 'Image')?.[1]
  return (
    <article className="flex h-full flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm transition duration-200 hover:-translate-y-1 hover:shadow-lg hover:cursor-pointer">
      <div className="flex items-center justify-between gap-2 bg-electric-600 px-4 py-2.5 text-white">
        <span className="truncate text-[10px] font-bold uppercase tracking-wider">{card.codigo}</span>
        <span className="text-[11px] font-bold uppercase tracking-wider">{card.marca}</span>
      </div>
      <div className="flex h-36 items-center justify-center border-b border-gray-100 bg-electric-50/40 p-3">
        {imagen ? (
          <img src={imagen} alt={card.nombre} loading="lazy" className="max-h-28 object-contain" />
        ) : (
          <ImagePlaceholder className="h-full w-full rounded-xl" />
        )}
      </div>
      <div className="flex flex-1 flex-col px-4 pb-3 pt-3">
        {(card.categoria || card.familia) && <p className="text-[11px] uppercase tracking-wide text-gray-500">{card.categoria || card.familia}</p>}
        <h4 className="mb-2 text-lg font-bold text-navy-900">{card.nombre}</h4>
        {(card.diff || card.descripcion) && <p className="mb-3 line-clamp-3 text-[13px] leading-snug text-gray-600">{card.diff || card.descripcion}</p>}
      </div>
      <div className="flex items-center justify-between gap-3 border-t border-gray-100 px-4 py-3 text-xs font-semibold">
        <Link
          to={`/catalogo/${card.id}`}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 font-bold text-electric-600 hover:text-electric-700"
        >
          {t('pro.card.viewDetails')} <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5" />
        </Link>
        <Link to="/contact" state="cotizar-equipo" className="text-navy-900 hover:text-electric-600">
          {t('pro.card.quote')} <span aria-hidden="true">&rarr;</span>
        </Link>
      </div>
      {card.swrya && (
        <div className="flex items-center gap-2 border-t border-orange-100 bg-orange-50 px-4 py-2">
          <img src={swryaLogo} alt="SWRYA" className="h-3.5 w-auto" />
          <span className="text-[10.5px] font-bold tracking-wide text-orange-700">{t('pro.card.swrya')}</span>
        </div>
      )}
    </article>
  )
}

export default function ProSelection() {
  const { t } = useTranslation('catalogo')
  const { products, loading, error } = useProSelection()
  const [familia, setFamilia] = useState('')
  const [search, setSearch] = useState('')

  const familias = useMemo(() => [...new Set(products.map((c) => c.familia).filter(Boolean))], [products])

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase()
    return products.filter((c) => {
      if (familia && c.familia !== familia) return false
      if (q && !`${c.marca} ${c.nombre} ${c.codigo} ${c.categoria}`.toLowerCase().includes(q)) return false
      return true
    })
  }, [products, familia, search])

  return (
    <section className="relative isolate pb-8 pt-4">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        {/* Cómo usar: tira compacta */}
        <ol className="mx-auto grid max-w-5xl gap-6 md:grid-cols-3 md:gap-0 md:divide-x md:divide-gray-200">
          {t('pro.howTo.steps', { returnObjects: true }).map((s, i) => (
            <li key={s.title} className="flex items-start gap-3 md:px-6">
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-navy-900 text-sm font-bold text-white">{i + 1}</span>
              <div>
                <h3 className="text-base font-bold text-navy-900">{s.title}</h3>
                <p className="mt-0.5 text-sm leading-snug text-gray-500">{s.text}</p>
              </div>
            </li>
          ))}
        </ol>

        {/* Elige tu operación */}
        <div className="mt-14 grid gap-5 md:grid-cols-3">
          {proVerticals.map((v) => {
            const count = products.filter((c) => verticalOf(c) === v.id).length
            const Tag = count > 0 ? 'a' : 'div'
            return (
              <Tag
                key={v.id}
                {...(count > 0 ? { href: `#${v.id}` } : {})}
                className={`group flex flex-col rounded-2xl border border-gray-200 bg-white p-6 shadow-sm transition duration-200 ${
                  count > 0 ? 'hover:-translate-y-1 hover:border-electric-300 hover:shadow-lg' : 'opacity-60'
                }`}
              >
                <span className="w-fit rounded-full bg-electric-50 px-3 py-1 text-xs font-bold text-electric-600">
                  {t('pro.operations.count', { count })}
                </span>
                <h2 className="mt-4 text-xl font-bold text-navy-900">{t(`pro.verticals.${v.id}.title`)}</h2>
                <p className="mt-2 flex-1 text-sm leading-relaxed text-gray-500">{t(`pro.verticals.${v.id}.desc`)}</p>
                {count > 0 && (
                  <span className="mt-5 inline-flex items-center gap-1 text-sm font-bold text-electric-600 transition-all group-hover:gap-2">
                    {t('pro.operations.cta')} <span aria-hidden="true">&darr;</span>
                  </span>
                )}
              </Tag>
            )
          })}
        </div>

        {/* Filtros */}
        <div className="mt-14 flex flex-col items-center gap-4 border-t border-gray-200 pt-8 lg:flex-row lg:justify-between">
          <div className="flex flex-wrap justify-center gap-2" role="group" aria-label={t('pro.filter.label')}>
            {familias.length > 1 && ['', ...familias].map((f) => (
              <button
                key={f || 'all'}
                type="button"
                onClick={() => setFamilia(f)}
                aria-pressed={familia === f}
                className={`rounded-full border-[1.5px] px-4 py-2 text-xs font-bold transition ${
                  familia === f ? 'border-electric-600 bg-electric-600 text-white' : 'border-gray-200 bg-white text-navy-900 hover:border-electric-500'
                }`}
              >
                {f || t('pro.filter.all')}
              </button>
            ))}
          </div>
          <div className="relative w-full max-w-xs">
            <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('pro.filter.searchPlaceholder')}
              aria-label={t('pro.filter.searchPlaceholder')}
              className="w-full rounded-full border-[1.5px] border-gray-200 bg-white py-2 pl-9 pr-4 text-xs text-navy-900 placeholder:text-gray-400 focus:border-electric-500 focus:outline-none"
            />
          </div>
        </div>
        <p className="mt-4 flex items-center justify-center gap-2 text-xs text-gray-500 lg:justify-start">
          <img src={swryaLogo} alt="SWRYA" className="h-3.5 w-auto" />
          <strong className="text-orange-700">{t('pro.swryaNote.label')}:</strong> {t('pro.swryaNote.text')}
        </p>

        {loading && <p className="mt-10 text-center text-sm text-gray-500">{t('pro.status.loading')}</p>}
        {error && <p className="mt-10 text-center text-sm text-red-700">{t('pro.status.error')}</p>}
        {!loading && !error && visible.length === 0 && <p className="mt-10 text-center text-sm text-gray-500">{t('pro.filter.empty')}</p>}

        {/* Verticales */}
        {proVerticals.map((v, vi) => {
          const inVertical = visible.filter((c) => verticalOf(c) === v.id)
          if (inVertical.length === 0) return null
          const presentLevels = LEVELS.filter((l) => inVertical.some((c) => c.level === l))
          return (
            <div key={v.id} id={v.id} className="scroll-mt-24 pt-16">
              <div className="relative isolate overflow-hidden rounded-3xl bg-gradient-to-br from-navy-900 to-navy-950 px-6 py-8 sm:px-10 sm:py-10">
                <div className="pointer-events-none absolute -right-16 -top-16 -z-10 h-64 w-64 rounded-full bg-electric-500/30 blur-3xl" aria-hidden="true" />
                <span className="text-xs font-bold uppercase tracking-[0.2em] text-neon-300">
                  {t('pro.operations.eyebrow', { n: String(vi + 1).padStart(2, '0') })}
                </span>
                <div className="mt-3 flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
                  <div className="max-w-3xl">
                    <h2 className="text-2xl font-bold text-white sm:text-4xl">{t(`pro.verticals.${v.id}.title`)}</h2>
                    <p className="mt-3 text-sm leading-relaxed text-white/70 sm:text-base">{t(`pro.verticals.${v.id}.desc`)}</p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {presentLevels.map((l) => (
                      <a
                        key={l}
                        href={`#${v.id}-n${l}`}
                        className="rounded-full border border-white/25 px-4 py-1.5 text-xs font-bold text-white transition hover:border-neon-300 hover:text-neon-300"
                      >
                        {t(`pro.levels.${l}.tag`)} · {t(`pro.levels.${l}.name`)}
                      </a>
                    ))}
                  </div>
                </div>
              </div>

              {presentLevels.map((l) => {
                const items = inVertical.filter((c) => c.level === l)
                return (
                  <div key={l} id={`${v.id}-n${l}`} className="scroll-mt-24 pt-10">
                    <div className="mb-6 flex flex-wrap items-center gap-x-4 gap-y-2">
                      <span className={`rounded-full px-4 py-1.5 text-xs font-bold tracking-wider ${levelStyles[l].badge}`}>
                        {t(`pro.levels.${l}.tag`)} · {t(`pro.levels.${l}.name`)}
                      </span>
                      <p className="text-sm text-gray-500">{t(`pro.levels.${l}.desc`)}</p>
                      <span className="hidden h-px flex-1 bg-gray-200 sm:block" aria-hidden="true" />
                      <span className="text-xs font-semibold text-gray-400">{t('pro.operations.count', { count: items.length })}</span>
                    </div>
                    <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                      {items.map((c) => <ProCard key={c.id} card={c} t={t} />)}
                    </div>
                  </div>
                )
              })}
            </div>
          )
        })}

        {/* ¿No lo encuentras? */}
        <div className="mt-20 flex flex-col items-center gap-5 rounded-3xl bg-gradient-to-br from-navy-900 to-navy-950 px-6 py-10 text-center sm:px-12">
          <div className="max-w-xl">
            <h2 className="text-xl font-bold text-white sm:text-2xl">{t('pro.request.title')}</h2>
            <p className="mt-2 text-sm text-white/70">{t('pro.request.text')}</p>
          </div>
          <Link to="/contact" state="cotizar-equipo" className="rounded-md bg-neon-300 px-6 py-3 text-sm font-semibold text-navy-900 transition hover:bg-neon-400">
            {t('pro.request.cta')} <span aria-hidden="true">&rarr;</span>
          </Link>
        </div>
      </div>
    </section>
  )
}
